<template>
  <div class="order" v-if="!!Object.keys(order).length">
    <!-- Header -->
    <div class="order-line">
      <h1 class="order-line-title">
        {{ $t('My order') }}
      </h1>
    </div>
    <!---->

    <!-- Content -->
    <div class="order-content" v-if="!loader">
      <!-- Order header -->
      <div class="order-content-header">
        <!-- Go back button -->
        <div class="order-content-header-block">
          <button class="order-content-header-block-btn" @click="backOrdersPage">
            {{ getPartner ? getPartner.title : partnerNameWithOrder }}
          </button>
        </div>
        <!---->

        <!-- Order about -->
        <div class="order-content-header-block">
          <p class="order-content-header-block-order-id">
            <span class="order-content-header-block-text order-id"># {{ order.connection_order_id }} </span>
            <span class="order-content-header-block-text order-date"> {{ order.created_at | date('L') }} </span>
            <span v-if="customerStatusLabel" class="order-content-header-block-text order-status"> {{ customerStatusLabel }} </span>
          </p>
          <span class="order-content-header-block-item-refund" v-if="getFullRefund">
            {{ $t('Funds returned') }}
          </span>
        </div>
        <!---->
      </div>
      <!---->

      <!-- Order slider -->
      <div class="order-content-slider">
        <div v-if="isAnywhere">
          <h5 class="my5">
            {{ $t('Comment to the order') }}
          </h5>
          <div v-if="order.comment">
            <p class="anywhere-comment">
              {{ order.comment }}
            </p>
          </div>
        </div>
        <div v-else-if="!items.length" class="order-content-slider-empty">
          <p>{{ $t('No products listed for this order') }}</p>
          <p v-if="order.comment" class="anywhere-comment">{{ order.comment }}</p>
        </div>
        <order-slider
          :config="config"
          :items="items"
          :slides-page="slidesPage"
          :order-id="orderId"
          v-else
        />
      </div>
      <!---->

      <!-- Order details -->
      <div class="order-content-details">
        <!-- Order location -->
        <div v-if="hasAddress" class="order-content-details-location">
          <div class="order-content-details-location-block">
            <div class="order-content-details-location-block-details" v-if="address">
              <template>
                <span class="order-content-details-location-block-details-text">
                  <span v-if="address.street">{{ address.street }}</span> <span v-if="address.house">{{ address.house }}</span>
                  <span v-if="address.apartment">
                    , {{ $t('apartment') }} {{ address.apartment }}
                  </span>
                </span>
                <span v-if="address.city" class="order-content-details-location-block-details-text">
                  {{ address.city }}
                </span>
              </template>
              <span class="order-content-details-location-block-details-text">
                {{ address.phone }}
              </span>
            </div>
            <div class="order-content-details-location-block-actions">
              <span class="order-content-details-location-block-actions-text">
                {{ $t('Shipping address') }}
              </span>
            </div>
          </div>
        </div>
        <!---->

        <!-- Order checkout -->
        <div class="order-content-details-checkout" v-if="!isAnywhere">
          <div class="order-content-details-checkout-block">
            <h3 class="order-content-details-checkout-block-title">
              {{ $t('Order amount') }}
            </h3>
            <div class="order-content-details-checkout-block-item">
              <span class="order-content-details-checkout-block-item-text"> {{ $t('In the amount of') }} </span>
              <span class="order-content-details-checkout-block-item-separator" />
              <span class="order-content-details-checkout-block-item-text">
                <b> {{ !getFullRefund ? order.subtotal : order.original_subtotal | price }} </b>
              </span>
            </div>
            <div class="order-content-details-checkout-block-item">
              <span class="order-content-details-checkout-block-item-text"> {{ $t('Delivery') }} </span>
              <span class="order-content-details-checkout-block-item-separator" />
              <span class="order-content-details-checkout-block-item-text">
                <b> {{ order.shipping_amount | price }} </b>
              </span>
            </div>
            <div class="order-content-details-checkout-block-item" v-if="checkOnlinePayment(order.payment_method_code)">
              <span class="order-content-details-checkout-block-item-text"> {{ $t('Fee') }} </span>
              <span class="order-content-details-checkout-block-item-separator" />
              <span class="order-content-details-checkout-block-item-text">
                <b> {{ displayIpayFee | price }} </b>
              </span>
            </div>
            <div class="order-content-details-checkout-block-item" v-if="getPartialReturnExist">
              <span class="order-content-details-checkout-block-item-text"> {{ $t('Refunded') }} </span>
              <span class="order-content-details-checkout-block-item-separator" />
              <span class="order-content-details-checkout-block-item-text">
                <b> {{ order.refunded | price }} </b>
              </span>
            </div>
            <div class="order-content-details-checkout-block-item" v-if="hasServiceFee">
              <span class="order-content-details-checkout-block-item-text"> {{ $t('Service Fee') }} </span>
              <span class="order-content-details-checkout-block-item-separator" />
              <span class="order-content-details-checkout-block-item-text">
                <b> {{ order.service_fee | price }} </b>
              </span>
            </div>
            <div class="order-content-details-checkout-block-item total">
              <span class="order-content-details-checkout-block-item-text">
                <b> {{ $t('Grand total') }} </b>
              </span>
              <span class="order-content-details-checkout-block-item-separator" />
              <span class="order-content-details-checkout-block-item-text">
                <b> {{ getOrderTotal | price }} </b>
              </span>
            </div>
          </div>
          <div class="order-content-details-checkout-actions"
               :class="{ 'dual': checkOnlinePayment(order.payment_method_code) && !order.is_paid }"
               v-if="order.partner && order.partner.hasOwnProperty('enabled') && order.partner.enabled && order.partner.active"
          >
            <button class="order-content-details-checkout-actions-btn"
                    @click.prevent="pay"
                    v-if="checkOnlinePayment(order.payment_method_code) && !order.is_paid"
            >
              {{ $t('Pay') }}
            </button>
            <button class="order-content-details-checkout-actions-btn"
                    @click.prevent="remakeOrder"
            >
              {{ $t('Repeat the order') }}
            </button>
          </div>
        </div>
      </div>
      <!---->
    </div>
    <!---->

    <reorder-list :order-id="order.id" :order="order" :products="productsForReorder" :count="count" />
    <pay-order-modal v-if="!order.is_paid" :order="order" />
  </div>
  <!---->
</template>

<script async>
import MyOrder from '@vue-storefront/core/compatibility/components/blocks/MyAccount/MyOrder';
import { mapGetters } from 'vuex';
import { isOnlinePaymentMethod } from 'theme/helpers/paymentMethod'

const ReorderList = () => import(/* webpackChunkName: "vsf-reorder-list" */ 'theme/components/theme/blocks/Order/ReorderList.vue');
const OrderSlider = () => import(/* webpackChunkName: "vsf-order-slider" */ 'theme/components/core/blocks/MyAccount/OrderSlider.vue');
const PayOrderModal = () => import(/* webpackChunkName: "vsf-pay-order-modal" */ 'theme/components/core/blocks/MyAccount/Order/PayOrderModal.vue');

export default {
  mixins: [MyOrder],
  components: {
    ReorderList,
    OrderSlider,
    PayOrderModal
  },
  data () {
    return {
      partner: {},
      productsForReorder: {},
      config: {
        'arrows': false,
        'infinite': false,
        'draggable': false,
        'cssEase': 'ease-out',
        'speed': 500,
        'slidesToShow': 5,
        'slidesToScroll': 5,
        perPage: 3,
        autoScaleSlider: true,
        'responsive': [
          {
            'breakpoint': 1200,
            'settings': {
              'slidesToShow': 4,
              'slidesToScroll': 4,
              'arrows': false
            }
          },
          {
            'breakpoint': 991,
            'settings': {
              'slidesToShow': 3,
              'slidesToScroll': 3,
              'arrows': false
            }
          },
          {
            'breakpoint': 768,
            'settings': {
              'slidesToShow': 2,
              'slidesToScroll': 2,
              'arrows': false
            }
          }
        ]
      },
      slidesPage: {
        desktop: 5,
        tablet: 4,
        mobileM: 3,
        mobileS: 2
      }
    }
  },
  async activated () {
    await this.loadOrderPage();
  },
  watch: {
    ipayFee () {
      if (!this.checkOnlinePayment(this.order.payment_method_code) && this.ipayFee > 0) {
        this.$store.dispatch('ipay-payment-user/resetFee')
      }
    }
  },
  computed: {
    ...mapGetters({
      getPartner: 'partners/getCurrentPartner',
      getOrder: 'user/getOrdersHistory',
      // Live pre-payment fee estimate, populated by PayOrderModal's own dispatch when
      // the customer opens it to pay an unpaid order — irrelevant once is_paid is
      // true, since order.ipay_fee is then the real, stable, already-charged fee.
      ipayFee: 'ipay-payment-user/getFee'
    }),
    address () {
      if (this.hasAddress) {
        return {
          street: this.order.addresses[0].street,
          house: this.order.addresses[0].house,
          apartment: this.order.addresses[0].apartment,
          city: this.order.addresses[0].city,
          phone: this.order.addresses[0].phone_number
        }
      }
      return {}
    },
    getOrderExist () {
      return Object.keys(this.order).length === 0
    },
    hasAddress () {
      return !this.getOrderExist && this.order.addresses[0];
    },
    hasServiceFee() {
      return (this.order.service_fee && this.order.service_fee > 0)
    },
    getOrderTotal () {
      const grandTotal = this.getFullRefund ? this.order.grand_total : this.order.original_grand_total
      return this.order.is_paid
        ? parseFloat(grandTotal).toFixed(2)
        : (parseFloat(grandTotal) + this.ipayFee).toFixed(2)
    },
    displayIpayFee () {
      return this.order.is_paid ? this.order.ipay_fee : this.ipayFee
    },
    isAnywhere () {
      return (!this.getPartner || this.getPartner.code === 'anywhere') && this.partnerCode === 'anywhere'
    },
    partnerCode () {
      return this.order.hasOwnProperty('partner') ? this.order.partner.code : this.order.partner_code
    },
    partnerNameWithOrder () {
      if (this.order) {
        return this.order.hasOwnProperty('partner') ? this.order.partner.name : this.order.partner_name
      }
      return ''
    },
    amountToPay () {
      return parseFloat(this.getFullRefund ? this.order.grand_total : this.order.original_grand_total).toFixed(4)
    },
    // Customer-facing status (received/preparing/on_the_way/delivered/on_hold/
    // cancelled — a curated set derived from the internal lifecycle state,
    // never the raw internal status). Falls back to the legacy status string
    // for an older/uncached API response that predates this field.
    customerStatusLabel () {
      const labels = {
        received: this.$t('Received'),
        preparing: this.$t('Preparing'),
        on_the_way: this.$t('On the way'),
        delivered: this.$t('Delivered'),
        on_hold: this.$t('On hold'),
        cancelled: this.$t('Cancelled')
      }
      if (this.order.customer_status && labels[this.order.customer_status]) {
        return labels[this.order.customer_status]
      }
      if (!this.order.status) return ''
      return this.order.status.charAt(0).toUpperCase() + this.order.status.slice(1)
    }
  },
  methods: {
    async pay () {
      this.$bus.$emit('modal-show', `pay-order-${this.order.id}`)
    },
    async remakeOrder () {
      await this.changeLocation({code: this.order.location_code, forceCategoriesLoad: true})
      await this.ensureCurrentPartner({ code: this.order.partner_code, locationCode: this.order.location_code })
      this.openModal(this.orderId)
    },
    backOrdersPage () {
      this.$router.push('/my-account/orders')
    },
    checkOnlinePayment (payment) {
      return isOnlinePaymentMethod(payment)
    }
  }
}
</script>

<style lang="scss" scoped>
  .order {
    display: flex;
    flex-direction: column;

    @media only screen and (max-width: 576px) {
      padding: 0 20px;
    }

    &-line {
      height: 60px;
      margin-bottom: 20px;

      display: flex;
      justify-content: space-between;
      align-items: center;

      @media only screen and (max-width: 768px) {
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
      }

      @media only screen and (max-width: 576px) {
        display: none;
      }

      &-title {
        margin: 0;

        color: #111;
        font-size: 20px;
        font-weight: 600;

        @media only screen and (max-width: 576px) {
          display: none;
        }
      }
    }

    &-content {

      &-header {
        display: flex;
        justify-content: space-between;
        align-items: center;

        &-block {
          display: flex;
          flex-direction: column;
          align-items: flex-end;

          &-order-id {
            margin: 0 0 4px 0;
          }

          &-text {
            &.order-id {
              margin-right: 10px;

              color: #111;
              font-size: 16px;
              font-weight: 600;
            }

            &.order-date {
              color: #4a4a4a;
              font-size: 14px;
            }
          }

          &-item-refund {
            color: #11bb2e;
            font-size: 14px;
            font-weight: 600;
            line-height: 1;
            display: inline-flex;
          }

          &-btn {
            padding: 10px 0 10px 20px;

            color: #111;
            font-size: 16px;

            background-image: url('../../../../assets/account/icon-back.png');
            background-position: left center;
            background-size: 14px;
            background-repeat: no-repeat;

            background-color: transparent;
            border: none;
            outline: none;
          }
        }
      }

      &-slider {
        margin: 20px 0;
      }

      &-details {
        display: flex;
        justify-content: space-between;

        @media only screen and (max-width: 991px) {
          flex-direction: column;
        }

        &-location {
          width: 40%;

          @media only screen and (max-width: 991px) {
            width: 100%;
            margin-bottom: 20px;
          }

          &-block {
            height: 160px;

            display: flex;
            flex-direction: column;
            justify-content: space-between;

            position: relative;

            border: 1px solid #11bb2e;
            border-radius: 5px;

            &-details {
              margin-top: 10px;
              margin-left: 10px;
              margin-right: 10px;
              padding-left: 25px;

              position: relative;

              display: flex;
              flex-direction: column;

              &:before {
                width: 15px;
                height: 18px;

                position: absolute;
                top: 0;
                left: 0;

                margin: auto;
                content: '';

                background-image: url('../../../../assets/account/icon-location-green.png');
                background-position: center;
                background-size: 15px 18px;
                background-repeat: no-repeat;
              }

              &-text {
                font-size: 14px;

                & + & {
                  margin-top: 10px;
                }
              }
            }

            &-actions {
              display: flex;
              justify-content: flex-end;
              align-items: flex-end;

              &-text {
                padding: 8px 16px;

                color: #fff;
                font-size: 12px;
                font-weight: 600;

                border-top-left-radius: 5px;
                border-bottom-right-radius: 5px;

                background-color: #11bb2e;
              }
            }
          }
        }

        &-checkout {
          width: 55%;

          @media only screen and (max-width: 991px) {
            width: 100%;
          }

          &-block {
            padding: 20px;

            background-color: #f5f5f5;
            border-radius: 5px;

            &-title {
              padding: 0;
              margin: 0 0 10px 0;

              color: #111;
              font-size: 21px;
              font-weight: 600;
              text-align: center;
            }

            &-item {
              display: flex;
              justify-content: space-between;
              align-items: flex-end;

              & + & {
                margin-top: 10px;
              }

              &:last-child {
                margin-top: 30px;

                & .order-content-details-checkout-block-item-text {
                  font-size: 18px;
                  font-weight: 600;
                }
              }

              &-separator {
                margin: 0 15px 5px 15px;
                flex: 1 auto;
                border-bottom: 1px dashed rgba(17, 17, 17, .11);
              }

              &-text {
                color: #111;
                font-size: 16px;
              }
            }
          }

          &-actions {
            display: flex;
            justify-content: center;

            @media only screen and (max-width: 392px) {
              flex-direction: column;
            }

            &.dual {
              & .order-content-details-checkout-actions-btn {
                padding: 16px 24px;

                @media only screen and (max-width: 392px) {
                  width: 100%;
                  padding: 16px 0 16px 0;
                  margin: 10px 0 0 0;
                }

                &:first-child {
                  margin-right: 10px;

                  @media only screen and (max-width: 392px) {
                    margin-right: 0;
                  }
                }
              }
            }

            &-btn {
              width: auto;
              padding: 16px 84px;
              margin-top: 20px;

              color: #fff;
              font-size: 16px;
              font-weight: 400;
              text-align: center;

              background-color: #11bb2e;
              border: none;
              border-radius: 5px;

              outline: none;

              &:hover {
                cursor: pointer;
                background-color: rgba(17, 187, 46, 0.8);
              }
            }
          }
        }
      }
    }
  }

  .funds {
    width: 100%;
    justify-content: flex-end;

    color: #111;
    font-size: 16px;
    font-weight: 600;
  }
  .anywhere-comment {
    word-break: break-word;
  }
  .order-content-slider-empty {
    color: #4a4a4a;
    font-size: 14px;
  }
</style>
